import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { CAMPAIGN_TYPES_VALUES } from 'containers/AwarenessCenter/constants'

import { CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA } from '../../WizardSpearPhishingCampaign/constants'
import { selectCampaignId } from '../selectors'

import {
  getEmailsError,
  getEmailsSuccess,
  getLandingDomainListError,
  getLandingDomainListSuccess,
  retrieveEmailError,
  retrieveEmailSuccess,
  spearPhishingCampaignDataPollingError,
  spearPhishingCampaignDataPollingSuccess,
  updateEmailError,
  updateEmailSuccess,
  updateParticipantsError,
  updateParticipantsSuccess,
} from './actions'
import {
  GET_EMAILS,
  GET_LANDING_DOMAIN_LIST,
  RETRIEVE_EMAIL,
  SPEAR_PHISHING_CAMPAIGN_DATA_POLLING,
  UPDATE_EMAIL,
  UPDATE_PARTICIPANTS,
} from './constants'
import { selectCreatedSpearPhishingCampaignId } from './selectors'

const getEmailsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_EMAILS.INITIATE,
      UPDATE_PARTICIPANTS.INITIATE,
      SPEAR_PHISHING_CAMPAIGN_DATA_POLLING.SUCCESS
    ),
    mergeMap((action) =>
      api.wizardCampaign
        .getEmails(
          state$.value.partnerOptions.selectedCompany?.value,
          selectCampaignId(state$.value) || selectCreatedSpearPhishingCampaignId(state$.value)
        )
        .pipe(
          mergeMap((response) => {
            // On an explicit emails fetch (e.g. revisiting a campaign) the same response
            // also feeds the Setup step participants list, otherwise its "See full list"
            // modal stays empty because generatedEmailsParticipants is only filled while
            // generation is polling. See IC-48791.
            if (action.type === GET_EMAILS.INITIATE) {
              return of(getEmailsSuccess(response), updateParticipantsSuccess(response))
            }
            return of(updateParticipantsSuccess(response))
          }),
          catchError((error) =>
            of(
              epicsErrorHandler(
                action.type === GET_EMAILS.INITIATE ? getEmailsError : updateParticipantsError,
                error,
                [400]
              )
            )
          ),
          takeUntil(action$.pipe(ofType(GET_EMAILS.CANCEL, UPDATE_PARTICIPANTS.CANCEL)))
        )
    )
  )

const updateEmailEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_EMAIL.INITIATE),
    mergeMap((action) =>
      api.wizardCampaign
        .updateEmail(
          action.payload.scenarioId,
          action.payload.data,
          state$.value.partnerOptions.selectedCompany?.value
        )
        .pipe(
          map((ajaxResponse) => updateEmailSuccess(ajaxResponse.response)),
          catchError((error) => of(epicsErrorHandler(updateEmailError, error, [400])))
        )
    )
  )

const retrieveEmailEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RETRIEVE_EMAIL.INITIATE),
    mergeMap((action) =>
      api.wizardCampaign
        .retrieveEmail(
          action.payload.scenarioId,
          state$.value.partnerOptions.selectedCompany?.value
        )
        .pipe(
          map((response) => retrieveEmailSuccess(response)),
          catchError((error) => of(epicsErrorHandler(retrieveEmailError, error, [400]))),
          takeUntil(action$.pipe(ofType(RETRIEVE_EMAIL.CANCEL)))
        )
    )
  )

const getDomainLandingEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_LANDING_DOMAIN_LIST.INITIATE),
    mergeMap(() =>
      api.awarenessCenter
        .details(
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner,
          CAMPAIGN_TYPES_VALUES.SPEAR_PHISHING
        )
        .pipe(
          map((response) => getLandingDomainListSuccess(response.landingDomainCategories)),
          catchError((error) => of(epicsErrorHandler(getLandingDomainListError, error, [400]))),
          takeUntil(action$.pipe(ofType(GET_LANDING_DOMAIN_LIST.CANCEL)))
        )
    )
  )

const spearPhishingCampaignDataPollingEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SPEAR_PHISHING_CAMPAIGN_DATA_POLLING.INITIATE),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSpearPhishingCampaign) return true
      return false
    }),
    mergeMap((action) =>
      api.wizardCampaign
        .getCampaign(
          state$.value.partnerOptions.selectedCompany?.value,
          selectCampaignId(state$.value) || action.payload.campaignId
        )
        .pipe(
          mergeMap((response) => of(spearPhishingCampaignDataPollingSuccess(response))),
          catchError((error) =>
            of(epicsErrorHandler(spearPhishingCampaignDataPollingError, error))
          ),
          takeUntil(
            action$.pipe(
              ofType(
                SPEAR_PHISHING_CAMPAIGN_DATA_POLLING.CANCEL,
                CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA
              )
            )
          )
        )
    )
  )

export default combineEpics(
  getEmailsEpic,
  getDomainLandingEpic,
  updateEmailEpic,
  retrieveEmailEpic,
  spearPhishingCampaignDataPollingEpic
)
